import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Eye, Radio, Trophy, User, Heart, Bell, BellOff, Star, ExternalLink, Share2, Flame, Target, Zap, Lock, Volume2, VolumeX } from 'lucide-react';
import WhosHotCard from '../components/shared/WhosHotCard';
import InstallAppPrompt from '../components/shared/InstallAppPrompt';
import { isNativeApp } from '../lib/platform';
import LiveStreamPlayer from '../components/shared/LiveStreamPlayer';
import LightningDelayBanner from '../components/shared/LightningDelayBanner';
import { cn } from '../lib/utils';
import playSounds from '../lib/playSounds';
import FamilyUpsellCard, { FamilyUpgradeDialog } from '../components/shared/FamilyUpsellCard';
import { toast } from 'sonner';
import { usePushNotifications } from '../hooks/usePushNotifications';

const API = process.env.REACT_APP_BACKEND_URL;

const getPublicGame = async (gameId) => {
  const res = await fetch(`${API}/api/games/public/${gameId}?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error('Game not found');
  return res.json();
};

const getTeam = async (teamId) => {
  const res = await fetch(`${API}/api/teams/public/${teamId}?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error('Team not found');
  return res.json();
};

// Field position coordinates on the diamond SVG
const FIELD_POS = {
  P:  { x: 200, y: 250 },
  C:  { x: 200, y: 370 },
  '1B': { x: 320, y: 250 },
  '2B': { x: 260, y: 175 },
  SS:  { x: 140, y: 175 },
  '3B': { x: 80, y: 250 },
  LF:  { x: 70, y: 100 },
  CF:  { x: 200, y: 55 },
  RF:  { x: 330, y: 100 },
};

// Scoreboard with TOP/BOT inning
const Scoreboard = ({ homeTeam, awayTeam, homeScore, awayScore, inning, isTopInning, outs, innings, opponentInnings, isHome }) => {
  const maxInnings = Math.max(innings?.length || 0, opponentInnings?.length || 0, 7);
  // Score-bump animation: when a score number ticks UP, we bump a key on that
  // number so React remounts the <p> and the CSS keyframe replays. On first
  // paint we DON'T animate (would look weird opening to a 6-6 game with both
  // numbers glowing). prevScoreRef stays null until the first poll completes,
  // so the initial render establishes a baseline silently.
  const prevScoresRef = useRef(null);
  const [homeBumpKey, setHomeBumpKey] = useState(0);
  const [awayBumpKey, setAwayBumpKey] = useState(0);
  useEffect(() => {
    if (prevScoresRef.current === null) {
      prevScoresRef.current = { home: homeScore, away: awayScore };
      return;
    }
    if (homeScore > prevScoresRef.current.home) setHomeBumpKey(k => k + 1);
    if (awayScore > prevScoresRef.current.away) setAwayBumpKey(k => k + 1);
    prevScoresRef.current = { home: homeScore, away: awayScore };
  }, [homeScore, awayScore]);

  return (
    <Card className="border-0 shadow-lg" data-testid="parent-scoreboard"
      style={{ background: 'linear-gradient(135deg, #131E36 0%, #1A2542 100%)', border: '1px solid rgba(212, 160, 58, 0.18)' }}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex-1">
            <p className="text-slate-400 text-[10px] uppercase tracking-wider">{awayTeam}</p>
            <p
              key={`away-${awayBumpKey}`}
              className={cn(
                "text-4xl md:text-5xl font-extrabold text-white leading-none",
                awayBumpKey > 0 && "ds-score-bump"
              )}
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              data-testid="away-score"
            >{awayScore}</p>
          </div>
          <div className="text-center px-4">
            <p className="text-sm font-bold" style={{ color: '#F5C455' }} data-testid="inning-display">
              {isTopInning ? 'TOP' : 'BOT'} {inning}
            </p>
            <div className="flex items-center justify-center gap-1 mt-1.5">
              {[0, 1, 2].map(i => (
                <div key={i} className={cn('w-3 h-3 rounded-full border', i < outs ? 'bg-ds-gold-bright border-ds-gold' : 'border-slate-600')} />
              ))}
            </div>
          </div>
          <div className="flex-1 text-right">
            <p className="text-slate-400 text-[10px] uppercase tracking-wider">{homeTeam}</p>
            <p
              key={`home-${homeBumpKey}`}
              className={cn(
                "text-4xl md:text-5xl font-extrabold text-white leading-none",
                homeBumpKey > 0 && "ds-score-bump"
              )}
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              data-testid="home-score"
            >{homeScore}</p>
          </div>
        </div>
        {/* Linescore */}
        <div className="overflow-x-auto">
          <div className="flex gap-0 text-[9px] min-w-fit">
            <div className="w-20 shrink-0" />
            {Array.from({ length: maxInnings }, (_, i) => (
              <div key={i} className="w-7 text-center text-stone-500 font-mono">{i + 1}</div>
            ))}
            <div className="w-7 text-center text-stone-500 font-bold">R</div>
          </div>
          {/* Away line */}
          <div className="flex gap-0 text-[9px]">
            <div className="w-20 shrink-0 text-stone-400 truncate">{awayTeam}</div>
            {Array.from({ length: maxInnings }, (_, i) => {
              const src = isHome ? opponentInnings : innings;
              const runs = src?.[i]?.runs;
              return <div key={i} className="w-7 text-center text-white font-mono">{runs != null ? runs : '-'}</div>;
            })}
            <div className="w-7 text-center text-amber-400 font-bold">{awayScore}</div>
          </div>
          {/* Home line */}
          <div className="flex gap-0 text-[9px]">
            <div className="w-20 shrink-0 text-stone-400 truncate">{homeTeam}</div>
            {Array.from({ length: maxInnings }, (_, i) => {
              const src = isHome ? innings : opponentInnings;
              const runs = src?.[i]?.runs;
              return <div key={i} className="w-7 text-center text-white font-mono">{runs != null ? runs : '-'}</div>;
            })}
            <div className="w-7 text-center text-amber-400 font-bold">{homeScore}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Animated runner tokens — slide base-to-base like a TV broadcast
const RUNNER_BASE_POS = {
  first: { left: '77.5%', top: '59.5%' },
  second: { left: '50%', top: '31%' },
  third: { left: '22.5%', top: '59.5%' },
  home: { left: '50%', top: '88%' },
};

const AnimatedRunners = ({ runners, players, totalScore }) => {
  const [tokens, setTokens] = useState([]);
  const initRef = useRef(false);
  const prevScoreRef = useRef(totalScore);

  useEffect(() => {
    const resolve = (r) => {
      if (!r) return null;
      if (typeof r === 'object') return { id: String(r.playerId || r.playerName || r.name), name: r.playerName || r.name || '?' };
      const p = players?.find(pl => pl.id === r);
      return { id: String(r), name: p ? p.name : (String(r).length > 24 ? '?' : String(r)) };
    };
    const current = {};
    ['first', 'second', 'third'].forEach(b => { const res = resolve(runners?.[b]); if (res) current[b] = res; });
    const scored = totalScore > prevScoreRef.current;
    prevScoreRef.current = totalScore;

    setTokens(prev => {
      if (!initRef.current) {
        initRef.current = true;
        return Object.entries(current).map(([base, res]) => ({ key: res.id, id: res.id, name: res.name, base, pos: RUNNER_BASE_POS[base], leaving: false, entering: false, scored: false }));
      }
      const live = prev.filter(t => !t.leaving);
      const next = [];
      const kept = new Set();
      Object.entries(current).forEach(([base, res]) => {
        const existing = live.find(t => t.id === res.id);
        if (existing) {
          kept.add(res.id);
          next.push({ ...existing, base, pos: RUNNER_BASE_POS[base] });
        }
      });
      live.forEach(t => {
        if (!kept.has(t.id)) next.push({ ...t, leaving: true, scored, pos: scored ? RUNNER_BASE_POS.home : t.pos });
      });
      prev.filter(t => t.leaving).forEach(t => next.push(t));
      Object.entries(current).forEach(([base, res]) => {
        if (!live.find(t => t.id === res.id)) {
          next.push({ key: `${res.id}:${Date.now()}`, id: res.id, name: res.name, base, pos: RUNNER_BASE_POS.home, entering: true, leaving: false, scored: false });
        }
      });
      return next;
    });
  }, [runners, totalScore, players]);

  useEffect(() => {
    if (tokens.some(t => t.entering)) {
      const raf = requestAnimationFrame(() => requestAnimationFrame(() => {
        setTokens(prev => prev.map(t => t.entering ? { ...t, entering: false, pos: RUNNER_BASE_POS[t.base] } : t));
      }));
      return () => cancelAnimationFrame(raf);
    }
    if (tokens.some(t => t.leaving)) {
      const timer = setTimeout(() => setTokens(prev => prev.filter(t => !t.leaving)), 1200);
      return () => clearTimeout(timer);
    }
  }, [tokens]);

  return (
    <>
      {tokens.map(t => (
        <div key={t.key}
          className="absolute pointer-events-none px-2 py-0.5 rounded-full border text-[10px] font-bold whitespace-nowrap z-10"
          style={{
            left: t.pos.left, top: t.pos.top,
            transform: 'translate(-50%, -150%)',
            background: 'rgba(10,19,34,0.9)', borderColor: '#f59e0b', color: '#fbbf24',
            opacity: t.leaving ? 0 : 1,
            transition: t.leaving && t.scored
              ? 'left 0.9s cubic-bezier(0.4,0,0.2,1), top 0.9s cubic-bezier(0.4,0,0.2,1), opacity 0.5s ease 0.55s'
              : 'left 0.9s cubic-bezier(0.4,0,0.2,1), top 0.9s cubic-bezier(0.4,0,0.2,1), opacity 0.5s ease',
          }}
          data-testid={`parent-runner-token-${t.base}`}>
          {(t.name || '').split(' ').pop()}
        </div>
      ))}
    </>
  );
};

// Large diamond with field positions and runners
const DiamondField = ({ runners, fieldPositions, players, currentPitcher, pitchCount, opponentFieldPositions, opponentPitcherName, opponentPitchCount, myTeamBatting, teamName, oppName, totalScore }) => {
  // When our team bats → opponent fields. When opponent bats → our team fields.
  const showOurFielding = !myTeamBatting;

  // Our team positions from field_positions array
  const ourPosMap = {};
  if (fieldPositions?.length > 0) {
    const latest = fieldPositions[fieldPositions.length - 1];
    latest?.positions?.forEach(p => {
      if (p.player_id) ourPosMap[p.position] = { name: p.player_name, id: p.player_id };
    });
  }

  // Opponent positions from opponent_field_positions dict
  const oppPosMap = {};
  if (opponentFieldPositions) {
    Object.entries(opponentFieldPositions).forEach(([pos, name]) => {
      if (name) oppPosMap[pos] = { name, id: null };
    });
  }

  const posMap = showOurFielding ? ourPosMap : oppPosMap;
  const fieldingTeam = showOurFielding ? teamName : oppName;
  const pitcherName = showOurFielding ? currentPitcher?.name : opponentPitcherName;
  const pitcherCount = showOurFielding ? (pitchCount || 0) : (opponentPitchCount || 0);
  const fieldColor = showOurFielding ? '#166534' : '#7c2d12';
  const fieldStroke = showOurFielding ? '#22c55e' : '#f97316';
  const nameColor = showOurFielding ? '#a3e635' : '#fdba74';

  return (
    <Card className="border-0 shadow-lg" data-testid="parent-diamond" style={{ background: '#131E36', border: '1px solid rgba(212, 160, 58, 0.15)' }}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <p className={cn("text-xs uppercase tracking-wider font-bold", showOurFielding ? "text-green-400" : "text-orange-400")} data-testid="fielding-team-label">
            {fieldingTeam || 'Team'} Fielding
          </p>
          {pitcherName && (
            <div className="flex items-center gap-2" data-testid="pitcher-info">
              <span className="text-[10px] text-stone-500">Pitching:</span>
              <span className="text-xs text-amber-400 font-bold">{pitcherName}</span>
              <span className="text-[10px] bg-amber-800 text-amber-200 px-1.5 py-0.5 rounded font-mono">{pitcherCount}P</span>
            </div>
          )}
        </div>
        <div className="relative w-full" style={{ paddingBottom: '100%', maxWidth: 400, margin: '0 auto' }}>
          <svg viewBox="0 0 400 420" className="absolute inset-0 w-full h-full">
            {/* Outfield grass */}
            <ellipse cx="200" cy="200" rx="185" ry="170" fill="#2d5a27" opacity="0.3" />
            {/* Infield dirt */}
            <polygon points="200,130 310,250 200,370 90,250" fill="#8B7355" opacity="0.2" />
            {/* Base paths */}
            <line x1="200" y1="370" x2="310" y2="250" stroke="#666" strokeWidth="1.5" />
            <line x1="310" y1="250" x2="200" y2="130" stroke="#666" strokeWidth="1.5" />
            <line x1="200" y1="130" x2="90" y2="250" stroke="#666" strokeWidth="1.5" />
            <line x1="90" y1="250" x2="200" y2="370" stroke="#666" strokeWidth="1.5" />
            {/* Pitcher's mound */}
            <circle cx="200" cy="250" r="8" fill="#8B7355" opacity="0.5" />

            {/* Bases */}
            <rect x="195" y="365" width="10" height="10" fill="white" transform="rotate(45 200 370)" />
            <rect x="305" y="245" width="10" height="10" fill={runners?.first ? '#f59e0b' : '#555'} transform="rotate(45 310 250)" />
            <rect x="195" y="125" width="10" height="10" fill={runners?.second ? '#f59e0b' : '#555'} transform="rotate(45 200 130)" />
            <rect x="85" y="245" width="10" height="10" fill={runners?.third ? '#f59e0b' : '#555'} transform="rotate(45 90 250)" />

            {/* Field position players (labels/names rendered as HTML overlays below) */}
            {Object.entries(FIELD_POS).map(([pos, coords]) => {
              const player = posMap[pos];
              return (
                <circle key={pos} cx={coords.x} cy={coords.y} r={12} fill={player ? fieldColor : '#333'} stroke={player ? fieldStroke : '#555'} strokeWidth="1.5" />
              );
            })}
          </svg>
          {Object.entries(FIELD_POS).map(([pos, coords]) => {
            const player = posMap[pos];
            return (
              <React.Fragment key={pos}>
                <span className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none text-white font-bold text-[9px] leading-none"
                  style={{ left: `${(coords.x / 400) * 100}%`, top: `${(coords.y / 420) * 100}%` }}>
                  {pos}
                </span>
                {player && (
                  <span className="absolute -translate-x-1/2 pointer-events-none text-[9px] leading-none whitespace-nowrap"
                    style={{ left: `${(coords.x / 400) * 100}%`, top: `${((coords.y + 17) / 420) * 100}%`, color: nameColor }}>
                    {player.name?.split(' ').pop() || ''}
                  </span>
                )}
              </React.Fragment>
            );
          })}
          <AnimatedRunners runners={runners} players={players} totalScore={totalScore} />
        </div>
      </CardContent>
    </Card>
  );
};

// Batting lineup with at-bat results — shows BOTH teams
const BattingLineup = ({ battingOrder, players, opponentLineup, playLog, currentBatterIndex, opponentBatterIndex, isTopInning, isHome, teamName, oppName }) => {
  // Calculate per-player at-bat stats from play_log by name matching
  const getPlayerStats = (playerId, playerName) => {
    if (!playLog?.length) return { summary: '', results: [] };
    const results = [];
    let hits = 0, totalAB = 0;
    
    playLog.forEach(entry => {
      const desc = entry.description || '';
      const pid = entry.playerId || entry.player_id;
      const matchById = pid && pid === playerId;
      const matchByName = !matchById && playerName && desc.includes(playerName);
      if (!matchById && !matchByName) return;
      
      if (desc.includes('Single')) { results.push('1B'); hits++; totalAB++; }
      else if (desc.includes('Double') && !desc.includes('Play')) { results.push('2B'); hits++; totalAB++; }
      else if (desc.includes('Triple') && !desc.includes('Play')) { results.push('3B'); hits++; totalAB++; }
      else if (desc.includes('Home Run') || desc.includes('HR')) { results.push('HR'); hits++; totalAB++; }
      else if (desc.includes('Walk') || desc.includes('BB')) { results.push('BB'); }
      else if (desc.includes('HBP')) { results.push('HBP'); }
      else if (desc.includes('Out') || desc.includes('Strikeout') || desc.includes('K')) { results.push('O'); totalAB++; }
      else if (desc.includes('Error')) { results.push('E'); totalAB++; }
      else if (desc.includes('Sacrifice') || desc.includes('SAC')) { results.push('SAC'); }
      else if (desc.includes('FC') || desc.includes("Fielder's Choice")) { results.push('FC'); totalAB++; }
    });
    
    const summary = totalAB > 0 ? `${hits}-${totalAB}` : '';
    return { summary, results };
  };

  // Determine which team is batting based on is_top_inning and is_home
  // Top inning = away team bats. If we are home, top = opponent bats. If we are away, top = our team bats.
  const myTeamBatting = isHome ? !isTopInning : isTopInning;

  const renderResultBadges = (results) => results.length > 0 && (
    <div className="flex gap-0.5 mt-0.5 flex-wrap">
      {results.map((r, idx) => (
        <span key={idx} className={cn('text-[8px] px-1 rounded font-mono',
          r === '1B' || r === '2B' || r === '3B' || r === 'HR' ? 'bg-green-900 text-green-300' :
          r === 'BB' || r === 'HBP' ? 'bg-blue-900 text-blue-300' :
          r === 'O' || r === 'K' ? 'bg-red-900/50 text-red-400' :
          'bg-stone-700 text-stone-400'
        )}>{r}</span>
      ))}
    </div>
  );

  return (
    <Card className="border-0 shadow-lg" data-testid="parent-lineup" style={{ background: '#131E36', border: '1px solid rgba(212, 160, 58, 0.15)' }}>
      <CardContent className="p-3">
        <div className="grid grid-cols-2 gap-3">
          {/* Our Team */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <div className={cn("w-2 h-2 rounded-full", myTeamBatting ? "bg-green-400 animate-pulse" : "bg-stone-600")} />
              <p className="text-green-400 text-[10px] font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {teamName || 'Our Team'} {myTeamBatting && <span className="text-[8px] bg-green-700 text-white px-1 rounded ml-1">BATTING</span>}
              </p>
            </div>
            <div className="space-y-0.5 max-h-[280px] overflow-y-auto">
              {battingOrder?.map((id, i) => {
                const player = players?.find(p => p.id === id);
                if (!player) return null;
                const isCurrent = myTeamBatting && i === currentBatterIndex;
                const stats = getPlayerStats(id, player.name);
                return (
                  <div key={id} data-testid={`lineup-player-${i}`} className={cn(
                    'flex items-center gap-1 py-1 px-1 rounded text-[10px]',
                    isCurrent ? 'bg-green-900/60 border border-green-600' : ''
                  )}>
                    <span className="w-4 text-right text-stone-600 font-mono">{i + 1}</span>
                    <div className="w-6 h-6 rounded bg-stone-700 flex items-center justify-center text-white font-bold text-[9px]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {player.jersey_number || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <span className={cn('truncate', isCurrent ? 'text-green-300 font-bold' : 'text-stone-400')}>
                          {player.name}
                        </span>
                        {stats.summary && <span className="text-amber-400 font-mono text-[8px] shrink-0">{stats.summary}</span>}
                      </div>
                      {renderResultBadges(stats.results)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Opponent Team */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <div className={cn("w-2 h-2 rounded-full", !myTeamBatting ? "bg-red-400 animate-pulse" : "bg-stone-600")} />
              <p className="text-red-400 text-[10px] font-bold uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {oppName || 'Opponent'} {!myTeamBatting && <span className="text-[8px] bg-red-700 text-white px-1 rounded ml-1">BATTING</span>}
              </p>
            </div>
            <div className="space-y-0.5 max-h-[280px] overflow-y-auto">
              {opponentLineup?.length > 0 ? opponentLineup.map((opp, i) => {
                const isCurrent = !myTeamBatting && i === (opponentBatterIndex || 0);
                const stats = getPlayerStats(null, opp.name);
                return (
                  <div key={i} data-testid={`opp-lineup-player-${i}`} className={cn(
                    'flex items-center gap-1 py-1 px-1 rounded text-[10px]',
                    isCurrent ? 'bg-red-900/60 border border-red-600' : ''
                  )}>
                    <span className="w-4 text-right text-stone-600 font-mono">{i + 1}</span>
                    <div className="w-6 h-6 rounded bg-stone-700 flex items-center justify-center text-white font-bold text-[9px]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {opp.number || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <span className={cn('truncate', isCurrent ? 'text-red-300 font-bold' : 'text-stone-400')}>
                          {opp.name}
                        </span>
                      </div>
                      {renderResultBadges(stats.results)}
                    </div>
                  </div>
                );
              }) : (
                <p className="text-stone-600 text-[10px] text-center py-4">No opponent lineup entered</p>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Enhanced Play-by-play log
const PlayLog = ({ playLog, gameId, parentName }) => {
  const logRef = useRef(null);
  const [likedPlays, setLikedPlays] = useState({});

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = 0;
    }
  }, [playLog?.length]);

  // Initialize liked state from play_log data
  useEffect(() => {
    if (!playLog?.length) return;
    const liked = {};
    playLog.forEach((entry, idx) => {
      if (entry.likes?.includes(parentName)) liked[idx] = true;
    });
    setLikedPlays(liked);
  }, [playLog, parentName]);

  const toggleLike = async (realIndex) => {
    if (!parentName || !gameId) return;
    const isLiked = likedPlays[realIndex];
    try {
      const endpoint = isLiked ? 'unlike-play' : 'like-play';
      await fetch(`${API}/api/games/${gameId}/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ play_index: realIndex, username: parentName })
      });
      setLikedPlays(prev => ({ ...prev, [realIndex]: !isLiked }));
    } catch {}
  };

  if (!playLog?.length) {
    return (
      <Card className="border-0 shadow-lg" data-testid="parent-play-log" style={{ background: '#131E36', border: '1px solid rgba(212, 160, 58, 0.15)' }}>
        <CardHeader className="pb-2">
          <CardTitle className="text-white text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>PLAY-BY-PLAY</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Eye size={28} className="mx-auto text-stone-600 mb-2" />
            <p className="text-stone-400 text-sm">No plays recorded yet</p>
            <p className="text-stone-600 text-xs mt-1">Plays will appear here as the game progresses</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const reversedLog = [...playLog].reverse();

  return (
    <Card className="border-0 shadow-lg" data-testid="parent-play-log" style={{ background: '#131E36', border: '1px solid rgba(212, 160, 58, 0.15)' }}>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-white text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          PLAY-BY-PLAY ({playLog.length})
        </CardTitle>
        <div className="flex items-center gap-1 text-green-400 text-xs">
          <Radio size={10} className="animate-pulse" />
          <span>Live</span>
        </div>
      </CardHeader>
      <CardContent>
        <div ref={logRef} className="space-y-1 max-h-[500px] overflow-y-auto" data-testid="play-log-list">
          {reversedLog.map((entry, idx) => {
            const realIndex = playLog.length - 1 - idx;
            const desc = entry.description || entry.text || '';
            const inning = entry.inning;
            const isTop = entry.isTopInning;
            const isInningHeader = desc.includes('Outs!') || desc.includes('Side retired');
            const likes = entry.likes || [];
            const isLiked = likedPlays[realIndex];
            
            return (
              <div key={entry.id || idx} className={cn(
                'flex items-start gap-2 py-1 px-2 rounded text-xs group',
                isInningHeader ? 'bg-amber-900/20 border-l-2 border-amber-500' : 'border-l-2 border-stone-700'
              )}>
                {inning && (
                  <span className="text-stone-500 text-[9px] font-mono w-8 shrink-0 pt-0.5">
                    {isTop !== undefined ? (isTop ? 'T' : 'B') : ''}{inning}
                  </span>
                )}
                <span className={cn(
                  'flex-1',
                  isInningHeader ? 'text-amber-400 font-semibold' :
                  desc.includes('Single') || desc.includes('Double') || desc.includes('Triple') || desc.includes('Home Run') ? 'text-green-400' :
                  desc.includes('Out') || desc.includes('Strikeout') ? 'text-red-400' :
                  desc.includes('Walk') || desc.includes('BB') || desc.includes('HBP') ? 'text-blue-400' :
                  desc.includes('Ball') ? 'text-blue-300' :
                  desc.includes('Strike') ? 'text-red-300' :
                  desc.includes('Score') || desc.includes('scores') ? 'text-amber-300' :
                  'text-stone-300'
                )}>
                  {desc}
                </span>
                {!isInningHeader && parentName && (
                  <button
                    onClick={() => toggleLike(realIndex)}
                    className="shrink-0 flex items-center gap-0.5 transition-all"
                    data-testid={`like-btn-${idx}`}
                  >
                    <Heart
                      size={14}
                      className={cn(
                        'transition-all',
                        isLiked ? 'fill-red-500 text-red-500 scale-110' : 'text-stone-600 hover:text-red-400'
                      )}
                    />
                    {likes.length > 0 && (
                      <span className="text-[9px] text-stone-500">{likes.length}</span>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// At-Bat Tracker — shows pitch-by-pitch zone for current at-bat + half-inning summary
const PitchZoneChart = ({ playLog, currentInning, isTopInning }) => {
  // Parse play log into half-innings, each containing at-bats
  const halfInnings = [];
  let currentHalf = null;
  let currentAB = null;
  let seed = 1;
  const rand = () => { seed = (seed * 16807 + 0) % 2147483647; return (seed - 1) / 2147483646; };

  const isResult = (d) => d.includes('Single') || d.includes('Double') || d.includes('Triple') || d.includes('Home Run') ||
    d.includes('Walk') || d.includes('BB') || d.includes('HBP') || d.includes('Strikeout') || d.includes('(K)') ||
    d.includes('Out') || d.includes('Error') || d.includes('FC') || d.includes("Fielder's Choice") || d.includes('Sacrifice');

  (playLog || []).forEach((entry, i) => {
    const d = entry.description || '';
    const inn = entry.inning;
    const top = entry.isTopInning ?? entry.isTop ?? null;
    if (inn == null) return;

    // Use 'X' for unknown half to avoid losing entries
    const halfKey = top !== null ? `${top ? 'T' : 'B'}${inn}` : `X${inn}`;
    if (!currentHalf || currentHalf.key !== halfKey) {
      if (currentAB && currentAB.pitches.length > 0) { if (currentHalf) currentHalf.atBats.push(currentAB); }
      currentHalf = { key: halfKey, inning: inn, isTop: top, atBats: [] };
      halfInnings.push(currentHalf);
      currentAB = { batter: '', pitches: [], result: null };
    }

    // Extract batter name from description
    const nameMatch = d.match(/- (.+?)(?:\s*\(|$)/);
    const batterName = nameMatch ? nameMatch[1].trim() : '';

    if (d.startsWith('Ball') && !d.includes('Balk')) {
      if (!currentAB.batter) currentAB.batter = batterName;
      seed = (i + 7) * 31;
      const side = rand();
      let x, y;
      if (side < 0.25) { x = 5 + rand() * 22; y = 20 + rand() * 55; }
      else if (side < 0.5) { x = 73 + rand() * 22; y = 20 + rand() * 55; }
      else if (side < 0.75) { x = 20 + rand() * 60; y = 3 + rand() * 14; }
      else { x = 20 + rand() * 60; y = 78 + rand() * 14; }
      currentAB.pitches.push({ type: 'ball', x, y, num: currentAB.pitches.length + 1 });
    } else if (d.startsWith('Strike') && !d.includes('Strikeout')) {
      if (!currentAB.batter) currentAB.batter = batterName;
      seed = (i + 7) * 31;
      currentAB.pitches.push({ type: 'strike', x: 32 + rand() * 36, y: 22 + rand() * 51, num: currentAB.pitches.length + 1 });
    } else if (d.startsWith('Foul Ball')) {
      if (!currentAB.batter) currentAB.batter = batterName;
      seed = (i + 7) * 31;
      currentAB.pitches.push({ type: 'foul', x: 32 + rand() * 36, y: 22 + rand() * 51, num: currentAB.pitches.length + 1 });
    } else if (isResult(d)) {
      if (!currentAB.batter) currentAB.batter = batterName;
      // Determine result label
      let res = '';
      if (d.includes('Strikeout') || d.includes('(K)')) res = 'K';
      else if (d.includes('Walk') || d.includes('BB')) res = 'BB';
      else if (d.includes('HBP')) res = 'HBP';
      else if (d.includes('Single')) res = '1B';
      else if (d.includes('Double') && !d.includes('Play')) res = '2B';
      else if (d.includes('Triple') && !d.includes('Play')) res = '3B';
      else if (d.includes('Home Run') || d.includes('HR')) res = 'HR';
      else if (d.includes('Error')) res = 'E';
      else if (d.includes('Sacrifice') || d.includes('SAC')) res = 'SAC';
      else if (d.includes('FC') || d.includes("Fielder's")) res = 'FC';
      else if (d.includes('Out')) res = 'OUT';
      currentAB.result = res;
      currentHalf.atBats.push(currentAB);
      currentAB = { batter: '', pitches: [], result: null };
    }
  });
  // Push last in-progress AB
  if (currentAB && (currentAB.pitches.length > 0 || currentAB.batter) && currentHalf) {
    currentHalf.atBats.push(currentAB);
  }

  if (halfInnings.length === 0) return null;

  // Find current half-inning — try exact key, then same inning, then last
  const currentKey = `${isTopInning ? 'T' : 'B'}${currentInning}`;
  let activeHalf = halfInnings.find(h => h.key === currentKey);
  if (!activeHalf) activeHalf = halfInnings.find(h => h.key === `X${currentInning}`);
  if (!activeHalf) activeHalf = halfInnings.filter(h => h.inning === currentInning).pop();
  if (!activeHalf) activeHalf = halfInnings[halfInnings.length - 1];
  if (!activeHalf) return null;
  const activeAB = activeHalf.atBats[activeHalf.atBats.length - 1];
  const completedABs = activeAB?.result ? activeHalf.atBats : activeHalf.atBats.slice(0, -1);
  const liveAB = activeAB && !activeAB.result ? activeAB : null;

  const balls = liveAB ? liveAB.pitches.filter(p => p.type === 'ball').length : 0;
  const strikes = liveAB ? liveAB.pitches.filter(p => p.type === 'strike' || p.type === 'foul').length : 0;

  return (
    <div>
      {/* Half-inning label */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-amber-400 text-[10px] font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          {activeHalf.isTop ? 'TOP' : 'BOT'} {activeHalf.inning}
        </span>
        <span className="text-stone-600 text-[9px]">{activeHalf.atBats.length} AB{activeHalf.atBats.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Live at-bat zone */}
      {liveAB && (
        <div className="mb-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-white text-xs font-bold truncate">{liveAB.batter || 'Batter'}</span>
            <span className="text-stone-400 text-[10px] font-mono">{balls}-{strikes}</span>
          </div>
          <svg viewBox="0 0 100 95" className="w-full max-w-[250px] mx-auto rounded-lg" style={{ background: '#292524' }}>
            <polygon points="40,90 50,95 60,90 58,87 42,87" fill="none" stroke="#555" strokeWidth="0.5" />
            <rect x="30" y="20" width="40" height="55" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="0.6" />
            <line x1="43.3" y1="20" x2="43.3" y2="75" stroke="rgba(255,255,255,0.08)" strokeWidth="0.3" />
            <line x1="56.6" y1="20" x2="56.6" y2="75" stroke="rgba(255,255,255,0.08)" strokeWidth="0.3" />
            <line x1="30" y1="38.3" x2="70" y2="38.3" stroke="rgba(255,255,255,0.08)" strokeWidth="0.3" />
            <line x1="30" y1="56.6" x2="70" y2="56.6" stroke="rgba(255,255,255,0.08)" strokeWidth="0.3" />
            {liveAB.pitches.map((p, i) => (
              <g key={i}>
                <circle cx={p.x} cy={p.y} r={5} fill={p.type === 'ball' ? '#22c55e' : p.type === 'foul' ? '#f59e0b' : '#ef4444'} opacity={0.9} stroke="white" strokeWidth="0.5" />
                <text x={p.x} y={p.y + 1.8} textAnchor="middle" fill="white" fontSize="4" fontWeight="bold">{p.num}</text>
              </g>
            ))}
          </svg>
        </div>
      )}

      {/* Completed at-bats this half-inning */}
      {completedABs.length > 0 && (
        <div className="space-y-0.5 mt-2">
          {completedABs.map((ab, i) => {
            const b = ab.pitches.filter(p => p.type === 'ball').length;
            const s = ab.pitches.filter(p => p.type === 'strike' || p.type === 'foul').length;
            const resColor = ab.result === 'K' || ab.result === 'OUT' ? 'text-red-400' :
              ab.result === 'BB' || ab.result === 'HBP' ? 'text-blue-400' :
              ab.result === '1B' || ab.result === '2B' || ab.result === '3B' || ab.result === 'HR' ? 'text-green-400' :
              'text-stone-400';
            return (
              <div key={i} className="flex items-center gap-2 text-[10px] py-0.5 px-1 rounded bg-stone-800/50">
                <span className="text-stone-600 w-3">{i + 1}</span>
                <span className="text-stone-300 truncate flex-1">{ab.batter || '?'}</span>
                <span className="text-stone-500 font-mono">{b}-{s}</span>
                <div className="flex gap-0.5">
                  {ab.pitches.map((p, j) => (
                    <div key={j} className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: p.type === 'ball' ? '#22c55e' : p.type === 'foul' ? '#f59e0b' : '#ef4444' }} />
                  ))}
                </div>
                <span className={cn("font-bold text-[9px] min-w-[20px] text-right", resColor)}>{ab.result}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Legend */}
      <div className="flex justify-center gap-3 mt-2">
        {[['Ball','#22c55e'],['Strike','#ef4444'],['Foul','#f59e0b']].map(([label, color]) => (
          <div key={label} className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-stone-500 text-[9px]">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== BOX SCORE ====================
const BoxScore = ({ game, team }) => {
  if (!game?.play_log?.length) return null;
  const playLog = game.play_log || [];
  const isHome = game.is_home !== false;
  const players = team?.players || [];
  const oppLineup = game.opponent_lineup || [];

  // Parse batting stats from play_log for both teams
  const parseBatting = (isOurTeam) => {
    const stats = {};
    const addStat = (name, id) => {
      if (!stats[name]) stats[name] = { name, id, ab: 0, r: 0, h: 0, rbi: 0, bb: 0, k: 0, hr: 0, d: 0, t: 0, hbp: 0, order: Object.keys(stats).length };
      return stats[name];
    };

    playLog.forEach(entry => {
      const desc = entry.description || '';
      const nameMatch = desc.match(/- (.+?)(?:\s*\(|$)/);
      if (!nameMatch) return;
      const bName = nameMatch[1].trim();

      // Determine if this play belongs to our team or opponent
      const isTop = entry.isTopInning ?? entry.isTop;
      const ourTeamBatting = isHome ? !isTop : isTop;
      if (isOurTeam !== ourTeamBatting) return;

      const s = addStat(bName, null);
      const runsMatch = desc.match(/\((\d+) R\)/);
      const runs = runsMatch ? parseInt(runsMatch[1]) : 0;

      if (desc.includes('Single')) { s.ab++; s.h++; s.rbi += runs; }
      else if (desc.includes('Double') && !desc.includes('Play') && !desc.includes('Ground Rule')) { s.ab++; s.h++; s.d++; s.rbi += runs; }
      else if (desc.includes('Ground Rule Double')) { s.ab++; s.h++; s.d++; s.rbi += runs; }
      else if (desc.includes('Triple') && !desc.includes('Play')) { s.ab++; s.h++; s.t++; s.rbi += runs; }
      else if (desc.includes('HOME RUN') || desc.includes('Home Run')) { s.ab++; s.h++; s.hr++; s.rbi += runs; }
      else if (desc.includes('Walk') || desc.includes('BB') || desc.includes('IBB')) { s.bb++; }
      else if (desc.includes('Strikeout') || desc.includes('(K)')) { s.ab++; s.k++; }
      else if (desc.includes('HBP')) { s.hbp++; }
      else if (desc.includes('Out') || desc.includes('Flyout') || desc.includes('Groundout') || desc.includes('Lineout') || desc.includes('FC') || desc.includes("Fielder's")) {
        if (!desc.includes('Pick Off') && !desc.includes('Sacrifice') && !desc.includes('Infield Fly')) s.ab++;
      }
      if (desc.includes('scores') || desc.includes('Score')) {
        // Check for runner scoring mentions
      }
    });

    return Object.values(stats).sort((a, b) => a.order - b.order);
  };

  // Parse pitching stats
  const parsePitching = (isOurTeam) => {
    const stats = {};
    const addStat = (name) => {
      if (!stats[name]) stats[name] = { name, ip: 0, h: 0, r: 0, er: 0, bb: 0, k: 0, pitches: 0, order: Object.keys(stats).length };
      return stats[name];
    };

    // Get pitcher info from game data
    if (isOurTeam) {
      const pitcherId = game.current_pitcher_id;
      const pitcher = pitcherId ? players.find(p => p.id === pitcherId) : null;
      if (pitcher) {
        const s = addStat(pitcher.name);
        s.pitches = game.pitch_count || 0;
        // Count stats from play_log when opponent was batting
        playLog.forEach(entry => {
          const desc = entry.description || '';
          const isTop = entry.isTopInning ?? entry.isTop;
          const oppBatting = isHome ? isTop : !isTop;
          if (!oppBatting) return;
          if (desc.includes('Single') || desc.includes('Double') || desc.includes('Triple') || desc.includes('HOME RUN')) s.h++;
          if (desc.includes('Walk') || desc.includes('BB')) s.bb++;
          if (desc.includes('Strikeout') || desc.includes('(K)')) s.k++;
          const runsMatch = desc.match(/\((\d+) R\)/);
          if (runsMatch) s.r += parseInt(runsMatch[1]);
        });
      }
    } else {
      const oppPitcher = game.opponent_pitcher_name;
      if (oppPitcher) {
        const s = addStat(oppPitcher);
        s.pitches = game.opponent_pitch_count || 0;
        playLog.forEach(entry => {
          const desc = entry.description || '';
          const isTop = entry.isTopInning ?? entry.isTop;
          const ourBatting = isHome ? !isTop : isTop;
          if (!ourBatting) return;
          if (desc.includes('Single') || desc.includes('Double') || desc.includes('Triple') || desc.includes('HOME RUN')) s.h++;
          if (desc.includes('Walk') || desc.includes('BB')) s.bb++;
          if (desc.includes('Strikeout') || desc.includes('(K)')) s.k++;
          const runsMatch = desc.match(/\((\d+) R\)/);
          if (runsMatch) s.r += parseInt(runsMatch[1]);
        });
      }
    }
    return Object.values(stats).sort((a, b) => a.order - b.order);
  };

  const ourBatting = parseBatting(true);
  const oppBatting = parseBatting(false);
  const ourPitching = parsePitching(true);
  const oppPitching = parsePitching(false);

  if (ourBatting.length === 0 && oppBatting.length === 0) return null;

  const BattingTable = ({ batters, label, color }) => (
    <div className="mb-4">
      <p className={cn("text-xs font-bold mb-1.5 uppercase", color)} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{label} Batting</p>
      <div className="overflow-x-auto">
        <table className="w-full text-[10px]">
          <thead>
            <tr className="text-stone-500 border-b border-stone-700">
              <th className="text-left py-1 pr-2 font-semibold">Player</th>
              <th className="text-center px-1 font-semibold">AB</th>
              <th className="text-center px-1 font-semibold">H</th>
              <th className="text-center px-1 font-semibold">R</th>
              <th className="text-center px-1 font-semibold">RBI</th>
              <th className="text-center px-1 font-semibold">2B</th>
              <th className="text-center px-1 font-semibold">HR</th>
              <th className="text-center px-1 font-semibold">BB</th>
              <th className="text-center px-1 font-semibold">K</th>
              <th className="text-center px-1 font-semibold">AVG</th>
            </tr>
          </thead>
          <tbody>
            {batters.map((b, i) => {
              const avg = b.ab > 0 ? (b.h / b.ab).toFixed(3) : '.000';
              return (
                <tr key={i} className="border-b border-stone-800 hover:bg-stone-800/30">
                  <td className="text-left py-1 pr-2 text-white font-medium truncate max-w-[100px]">{b.name}</td>
                  <td className="text-center px-1 text-stone-300">{b.ab}</td>
                  <td className={cn("text-center px-1 font-bold", b.h > 0 ? "text-green-400" : "text-stone-500")}>{b.h}</td>
                  <td className={cn("text-center px-1", b.r > 0 ? "text-amber-400" : "text-stone-500")}>{b.r}</td>
                  <td className={cn("text-center px-1", b.rbi > 0 ? "text-amber-400" : "text-stone-500")}>{b.rbi}</td>
                  <td className="text-center px-1 text-stone-400">{b.d || '-'}</td>
                  <td className={cn("text-center px-1 font-bold", b.hr > 0 ? "text-red-400" : "text-stone-500")}>{b.hr || '-'}</td>
                  <td className="text-center px-1 text-stone-400">{b.bb || '-'}</td>
                  <td className="text-center px-1 text-stone-400">{b.k || '-'}</td>
                  <td className="text-center px-1 text-amber-300 font-mono">{avg}</td>
                </tr>
              );
            })}
            {/* Totals row */}
            {batters.length > 1 && (() => {
              const t = batters.reduce((a, b) => ({ ab: a.ab+b.ab, h: a.h+b.h, r: a.r+b.r, rbi: a.rbi+b.rbi, d: a.d+b.d, hr: a.hr+b.hr, bb: a.bb+b.bb, k: a.k+b.k }), { ab:0, h:0, r:0, rbi:0, d:0, hr:0, bb:0, k:0 });
              const avg = t.ab > 0 ? (t.h / t.ab).toFixed(3) : '.000';
              return (
                <tr className="border-t border-stone-600 font-bold">
                  <td className="text-left py-1 pr-2 text-stone-400">TOTAL</td>
                  <td className="text-center px-1 text-stone-300">{t.ab}</td>
                  <td className="text-center px-1 text-green-400">{t.h}</td>
                  <td className="text-center px-1 text-amber-400">{t.r}</td>
                  <td className="text-center px-1 text-amber-400">{t.rbi}</td>
                  <td className="text-center px-1 text-stone-400">{t.d || '-'}</td>
                  <td className="text-center px-1 text-red-400">{t.hr || '-'}</td>
                  <td className="text-center px-1 text-stone-400">{t.bb || '-'}</td>
                  <td className="text-center px-1 text-stone-400">{t.k || '-'}</td>
                  <td className="text-center px-1 text-amber-300 font-mono">{avg}</td>
                </tr>
              );
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );

  const PitchingTable = ({ pitchers, label, color }) => {
    if (pitchers.length === 0) return null;
    return (
      <div className="mb-2">
        <p className={cn("text-xs font-bold mb-1.5 uppercase", color)} style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{label} Pitching</p>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="text-stone-500 border-b border-stone-700">
                <th className="text-left py-1 pr-2 font-semibold">Pitcher</th>
                <th className="text-center px-1.5 font-semibold">P</th>
                <th className="text-center px-1.5 font-semibold">H</th>
                <th className="text-center px-1.5 font-semibold">R</th>
                <th className="text-center px-1.5 font-semibold">BB</th>
                <th className="text-center px-1.5 font-semibold">K</th>
              </tr>
            </thead>
            <tbody>
              {pitchers.map((p, i) => (
                <tr key={i} className="border-b border-stone-800">
                  <td className="text-left py-1 pr-2 text-white font-medium">{p.name}</td>
                  <td className="text-center px-1.5 text-amber-300 font-bold font-mono">{p.pitches}</td>
                  <td className="text-center px-1.5 text-stone-300">{p.h}</td>
                  <td className={cn("text-center px-1.5", p.r > 0 ? "text-red-400" : "text-stone-500")}>{p.r}</td>
                  <td className="text-center px-1.5 text-stone-400">{p.bb}</td>
                  <td className={cn("text-center px-1.5 font-bold", p.k > 0 ? "text-green-400" : "text-stone-500")}>{p.k}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <Card className="border-0 bg-stone-800 shadow-lg" data-testid="parent-box-score">
      <CardHeader className="pb-1">
        <CardTitle className="text-white text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          BOX SCORE
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3">
        {ourBatting.length > 0 && <BattingTable batters={ourBatting} label={team?.name || 'Your Team'} color="text-green-400" />}
        {ourPitching.length > 0 && <PitchingTable pitchers={ourPitching} label={team?.name || 'Your Team'} color="text-green-400" />}
        {oppBatting.length > 0 && <BattingTable batters={oppBatting} label={game?.opponent_name || 'Opponent'} color="text-red-400" />}
        {oppPitching.length > 0 && <PitchingTable pitchers={oppPitching} label={game?.opponent_name || 'Opponent'} color="text-red-400" />}
      </CardContent>
    </Card>
  );
};

export default function ParentViewPage() {
  const { teamId, gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  // Tracks the newest play we've already shown so we can flash the chip when
  // a fresh one arrives. Stored as the play.id (or a hash of description+time)
  // so refreshes that don't add anything don't trigger a flash.
  const lastSeenPlayKeyRef = useRef(null);
  const [flashKey, setFlashKey] = useState(0); // bumps each new play → triggers the CSS class
  // Sound toggle — muted by default. Initialized from localStorage so the
  // preference persists across visits.
  const [soundsOn, setSoundsOn] = useState(() => playSounds.isEnabled());
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [parentName, setParentName] = useState(() => localStorage.getItem('dmp_parent_name') || '');
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [myKids, setMyKids] = useState([]);
  const [shareToken, setShareToken] = useState(null);
  const [showKidPicker, setShowKidPicker] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [teamPremium, setTeamPremium] = useState(null);
  // Pinned coach messages — surfaced as a sticky banner at top of Parent View.
  // Loaded from the existing /api/teams/{team_id}/messages endpoint and
  // filtered to message.pinned === true.
  const [pinnedMessages, setPinnedMessages] = useState([]);
  // Family / Parent paywall state. `parentInfo` holds the logged-in parent's
  // /api/auth/me response. `parentIsPremium` is derived from it — true if
  // admin OR has active subscription OR active welcome/family trial.
  // Public viewers (no token) stay null → all premium features stay locked.
  const [parentInfo, setParentInfo] = useState(null);
  const parentIsPremium = !!parentInfo?.is_family_premium;
  const familyTrialDaysLeft = parentInfo?.family_trial_days_left || 0;
  // Single instance of the Family upgrade dialog rendered at the bottom of
  // the page so any locked-feature click can open it without owning its own
  // dialog state.
  const [showFamilyUpsell, setShowFamilyUpsell] = useState(false);
  const { supported: pushSupported, subscription: pushSub, subscribe: pushSubscribe, unsubscribe: pushUnsubscribe } = usePushNotifications();

  // Coach premium status gates push signup (public endpoint)
  useEffect(() => {
    if (!teamId) return;
    fetch(`${API}/api/teams/${teamId}/premium-status`, { cache: 'no-store' })
      .then(r => r.ok ? r.json() : null)
      .then(d => setTeamPremium(!!d?.is_premium))
      .catch(() => setTeamPremium(false));
  }, [teamId]);

  useEffect(() => {
    if (gameId && teamId) loadData();
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, [gameId, teamId]);

  // Fetch forecast once we know the team's saved weather location and the game date
  useEffect(() => {
    if (!team?.weather_lat || !team?.weather_lon || !game?.date) { setForecast(null); return; }
    if (game.is_complete) { setForecast(null); return; }
    fetch(`${API}/api/weather?lat=${team.weather_lat}&lon=${team.weather_lon}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.daily) return;
        import('../lib/weatherCodes').then(({ lookupForecast }) => {
          setForecast(lookupForecast({
            dates: data.daily.time || [],
            probs: data.daily.precipitation_probability_max || [],
            codes: data.daily.weather_code || [],
            tmax: data.daily.temperature_2m_max || [],
            tmin: data.daily.temperature_2m_min || [],
          }, game.date));
        });
      })
      .catch(() => setForecast(null));
  }, [team?.weather_lat, team?.weather_lon, game?.date, game?.is_complete]);

  // Load milestones once
  useEffect(() => {
    if (teamId) {
      fetch(`${API}/api/milestones/${teamId}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d?.milestones) setMilestones(d.milestones); })
        .catch(() => {});
    }
  }, [teamId]);

  // Resolve the viewer's premium status if they're logged in. Public viewers
  // (no token in localStorage) stay null → free tier. Re-fetches when the
  // parent links kids (the link-kids call kicks off the 3-day family trial).
  useEffect(() => {
    let cancelled = false;
    const fetchMe = () => {
      let token = null;
      try {
        const raw = localStorage.getItem('diamond-manager-auth');
        token = raw ? (JSON.parse(raw)?.state?.token || null) : null;
      } catch { /* not logged in */ }
      if (!token) { setParentInfo(null); return; }
      fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (!cancelled) setParentInfo(d); })
        .catch(() => { if (!cancelled) setParentInfo(null); });
    };
    fetchMe();
    return () => { cancelled = true; };
  }, [shareToken]);  // re-fetch after a link-kids call sets a new shareToken

  // ─── New-play flash detector ───
  // Compare the newest play's stable key on every refresh; if it changed, bump
  // flashKey so the leftmost chip in the Active Plays ticker plays the glow.
  // First-page-load is a NO-FLASH path — we just stamp the ref so refresh #2 onward
  // is the only thing that can trigger animation.
  useEffect(() => {
    const log = game?.play_log;
    if (!Array.isArray(log) || log.length === 0) return;
    const last = log[log.length - 1];
    const key = last?.id || `${last?.time || ''}|${last?.description || ''}`;
    if (lastSeenPlayKeyRef.current === null) {
      // First load — remember without flashing
      lastSeenPlayKeyRef.current = key;
      return;
    }
    if (key !== lastSeenPlayKeyRef.current) {
      lastSeenPlayKeyRef.current = key;
      setFlashKey(k => k + 1);
      // Optional audio — fires only when sounds are explicitly enabled. The
      // classification mirrors the chip-accent logic below so the sound always
      // matches the visual cue.
      const desc = (last?.description || last?.text || '').toLowerCase();
      const isHit  = /(single|double|triple|home run|hr\b|\bhit\b)/.test(desc);
      const isOut  = /(\bout\b|strikeout|\bk\b|fly out|ground out|line out|pop out|infield fly|triple play|caught|tagged)/.test(desc);
      if (isHit) playSounds.hit();
      else if (isOut) playSounds.out();
    }
  }, [game?.play_log]);

  // Load pinned coach messages — public, lightweight, polled every 30 s so a
  // pin / unpin from the coach side surfaces here within a window.
  useEffect(() => {
    if (!teamId) return;
    const fetchPins = () => {
      fetch(`${API}/api/teams/${teamId}/messages/pinned`, { cache: 'no-store' })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (Array.isArray(d?.messages)) setPinnedMessages(d.messages); })
        .catch(() => {});
    };
    fetchPins();
    const iv = setInterval(fetchPins, 30000);
    return () => clearInterval(iv);
  }, [teamId]);

  // Load linked kids when parent name changes
  useEffect(() => {
    if (parentName && teamId) {
      fetch(`${API}/api/highlights/my-kids/${teamId}?parent_name=${encodeURIComponent(parentName)}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) { setMyKids(d.player_ids || []); setShareToken(d.share_token); } })
        .catch(() => {});
    }
  }, [parentName, teamId]);

  const loadData = async () => {
    try {
      const [gameData, teamData] = await Promise.all([
        getPublicGame(gameId),
        getTeam(teamId)
      ]);
      setGame(gameData);
      setTeam(teamData);
      setLastRefresh(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Current batter - side aware
  const isTopInning = game?.is_top_inning ?? true;
  const isHome = game?.is_home !== false;
  const myTeamBatting = isHome ? !isTopInning : isTopInning;
  const currentBatterIndex = game?.current_batter_index ?? 0;
  const opponentBatterIndex = game?.opponent_current_batter_index ?? 0;
  const currentBatterId = myTeamBatting ? game?.batting_order?.[currentBatterIndex] : null;
  const currentBatter = currentBatterId ? team?.players?.find(p => p.id === currentBatterId) : null;
  const currentOppBatter = !myTeamBatting ? game?.opponent_lineup?.[opponentBatterIndex] : null;

  // === "MY KID IS UP" detection ===
  // True only when one of the parent's linked roster IDs is currently at the
  // plate. Also computes "on deck" so we can show a softer banner one batter
  // before the kid bats (parents love the heads-up).
  const onDeckBatterId = myTeamBatting && Array.isArray(game?.batting_order)
    ? game.batting_order[(currentBatterIndex + 1) % game.batting_order.length]
    : null;
  const kidAtBat = myKids.length > 0 && currentBatterId && myKids.includes(currentBatterId)
    ? team?.players?.find(p => p.id === currentBatterId)
    : null;
  const kidOnDeck = myKids.length > 0 && onDeckBatterId && myKids.includes(onDeckBatterId)
    ? team?.players?.find(p => p.id === onDeckBatterId)
    : null;

  // Current pitcher
  const currentPitcher = game?.current_pitcher_id ? team?.players?.find(p => p.id === game.current_pitcher_id) : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A1322' }}>
        <div className="w-12 h-12 border-4 rounded-full animate-spin" style={{ borderColor: '#2EBE6B', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-3 md:p-6" style={{ background: 'radial-gradient(ellipse at top, #131E36 0%, #0A1322 70%)' }} data-testid="parent-view-page">
      {/* Watch Live floating jump button — only when stream is currently broadcasting */}
      {game?.live_stream_url && game?.live_stream_active && (
        <button
          type="button"
          onClick={() => {
            // Fire analytics beacon
            try {
              const url = `${API}/api/stream-views/track`;
              const body = JSON.stringify({ game_id: game.id, kind: 'click' });
              if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
              else fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true });
            } catch {}
            const el = document.getElementById('live-feed');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          className="fixed bottom-3 right-3 z-40 bg-red-500 hover:bg-red-400 active:bg-red-600 text-white font-bold text-xs uppercase tracking-wider px-3 py-2 rounded-full shadow-2xl border-2 border-red-300 flex items-center gap-1.5 transition-transform hover:scale-105 active:scale-95"
          data-testid="parent-watch-live-btn"
          aria-label="Jump to live video"
        >
          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
          Watch Live
        </button>
      )}
      <div className="max-w-6xl mx-auto space-y-3">
        {/* Lightning / weather delay banner — shows when coach has declared a delay. */}
        <LightningDelayBanner lightningDelay={game?.lightning_delay} opponentName={game?.opponent_name} />

        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" className="text-stone-400 hover:text-white" onClick={() => navigate('/parent-browse')} data-testid="back-btn">
            <ArrowLeft size={16} className="mr-1" /> Back
          </Button>
          <div className="flex-1 text-center">
            <div className="flex items-center justify-center gap-2">
              <Eye size={18} className="text-green-500" />
              <h1 className="text-lg font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                GAME VIEW
              </h1>
            </div>
            <p className="text-stone-500 text-[10px]">
              {team?.name} vs {game?.opponent_name} {lastRefresh && <span className="text-green-500 ml-1">Updated: {lastRefresh}</span>}
            </p>
            {forecast && (
              <p className="flex items-center gap-1.5 text-[10px] text-stone-400 mt-0.5" data-testid="parent-forecast">
                {(() => { const F = forecast.info.Icon; return <F size={10} className={forecast.info.color} />; })()}
                <span>{forecast.info.label}</span>
                {typeof forecast.tmax === 'number' && typeof forecast.tmin === 'number' && (
                  <span className="text-stone-500">· {Math.round(forecast.tmax)}°/{Math.round(forecast.tmin)}°</span>
                )}
                {forecast.prob >= 20 && (
                  <span className={forecast.prob >= 40 ? 'text-amber-400 font-semibold' : 'text-blue-400'}>
                    · {forecast.prob}% rain
                  </span>
                )}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            {game?.is_complete ? (
              <Button size="sm" className="bg-amber-600 hover:bg-amber-500 text-xs" onClick={() => navigate(`/game-summary/${teamId}/${gameId}`)} data-testid="view-summary-btn">
                <Trophy size={12} className="mr-1" /> Summary
              </Button>
            ) : (
              <div className="flex items-center gap-1 text-green-500">
                <Radio size={12} className="animate-pulse" />
                <span className="text-xs font-semibold">LIVE</span>
              </div>
            )}
          </div>
        </div>

        {/* Install App Prompt - helps parents add app to homescreen */}
        <InstallAppPrompt variant="banner" />

        {/* Live video stream — shown only if coach has set a live_stream_url.
            When live_stream_active=false, the player turns into a compact "Replay" link.
            Family-tier locked: non-premium parents see the upsell teaser. */}
        {game?.live_stream_url && (
          parentIsPremium ? (
            <LiveStreamPlayer
              streamUrl={game.live_stream_url}
              active={!!game.live_stream_active}
              gameId={game.id}
              gameLabel={`${team?.name || ''} vs ${game?.opponent_name || ''}`}
            />
          ) : (
            <FamilyUpsellCard
              feature={game.live_stream_active ? '🔴 Coach is broadcasting live' : '📹 Coach posted a game replay'}
              blurb="Watch the live video stream right inside the app."
              testid="live-stream-locked"
            />
          )
        )}
        {/* === GAME HYPE REEL — replaces scoreboard hero when the game is complete ===
            One-tap entry into the auto-generated game summary + share button.
            Built on top of the existing /game-summary route + game.game_summary_story
            and a play-log-derived "winner" calculation. */}
        {game?.is_complete && (
          <Card className="border-0 shadow-2xl overflow-hidden"
            data-testid="hype-reel-card"
            style={{
              background: 'linear-gradient(135deg, #0E6B36 0%, #1FA85A 55%, #D4A03A 130%)',
              border: '1px solid rgba(212, 160, 58, 0.55)',
            }}>
            <CardContent className="p-5">
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-14 h-14 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(10, 19, 34, 0.5)', border: '2px solid rgba(245, 196, 85, 0.7)' }}>
                  <Trophy size={26} style={{ color: '#F5C455' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: '#F5C455' }}>FINAL · GAME COMPLETE</p>
                  <p className="text-2xl md:text-3xl font-extrabold text-white leading-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {(() => {
                      const isHome2 = game?.is_home;
                      const ours = isHome2 ? (game?.home_score || 0) : (game?.away_score || 0);
                      const theirs = isHome2 ? (game?.away_score || 0) : (game?.home_score || 0);
                      const result = ours > theirs ? 'W' : ours < theirs ? 'L' : 'T';
                      return `${result === 'W' ? '🏆 ' : ''}${team?.name || 'Us'} ${ours}, ${game?.opponent_name || 'Opp'} ${theirs}`;
                    })()}
                  </p>
                  <p className="text-emerald-50 text-sm mt-1.5">Tap below for the AI recap + share the highlight with family.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                <Button
                  size="lg"
                  className="font-bold text-stone-900 shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #F5C455 0%, #D4A03A 100%)' }}
                  onClick={() => navigate(`/game-summary/${teamId}/${gameId}`)}
                  data-testid="hype-reel-view-btn"
                >
                  <Trophy size={16} className="mr-1.5" /> View Highlights
                </Button>
                <Button
                  size="lg"
                  className="font-bold text-white shadow-lg"
                  style={{ background: 'rgba(10, 19, 34, 0.65)', border: '1px solid rgba(245, 196, 85, 0.5)' }}
                  data-testid="hype-reel-share-btn"
                  onClick={() => {
                    const isHome2 = game?.is_home;
                    const ours = isHome2 ? (game?.home_score || 0) : (game?.away_score || 0);
                    const theirs = isHome2 ? (game?.away_score || 0) : (game?.home_score || 0);
                    const result = ours > theirs ? 'W' : ours < theirs ? 'L' : 'T';
                    const emoji = result === 'W' ? '🏆' : result === 'L' ? '⚾' : '🤝';
                    const url = `${window.location.origin}/parent-view/${teamId}/${gameId}`;
                    const text = `${emoji} FINAL: ${team?.name || 'Us'} ${ours}, ${game?.opponent_name || 'Opp'} ${theirs}`;
                    if (navigator.share) {
                      navigator.share({ title: text, text, url }).catch(() => {});
                    } else {
                      navigator.clipboard.writeText(`${text}\n${url}`);
                      toast.success('Final score copied — paste it anywhere!');
                    }
                  }}
                >
                  <Share2 size={16} className="mr-1.5" /> Share Final
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* === PINNED COACH MESSAGES === */}
        {/* Free tier sees a slim teaser when pinned messages exist on the team
            but the parent isn't premium — so they know what they're missing.
            Logged-in premium / admin sees the actual banner. Public viewers
            (no token) just see the upsell variant. */}
        {pinnedMessages.length > 0 && (
          parentIsPremium ? (
            <div className="space-y-2" data-testid="pinned-messages">
              {pinnedMessages.slice(0, 3).map(m => (
                <Card key={m.id} className="border-0 shadow-md overflow-hidden"
                  style={{
                    background: 'linear-gradient(90deg, rgba(212, 160, 58, 0.18) 0%, rgba(26, 37, 66, 0.95) 60%)',
                    border: '1px solid rgba(245, 196, 85, 0.55)',
                  }}>
                  <CardContent className="p-3 flex items-start gap-3">
                    <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(245, 196, 85, 0.18)', border: '1px solid rgba(245, 196, 85, 0.7)' }}>
                      <span className="text-base" role="img" aria-label="Pin">📌</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#F5C455' }}>
                        Coach Note · {m.author_name || 'Coach'}
                      </p>
                      <p className="text-white text-sm leading-snug mt-0.5">{m.body || m.text || ''}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <FamilyUpsellCard
              feature="Coach pinned a note for parents"
              blurb={`${pinnedMessages.length} pinned ${pinnedMessages.length === 1 ? 'message' : 'messages'} · field changes, rain delays, etc. Unlock to see.`}
              testid="pinned-messages-locked"
            />
          )
        )}

        {/* === "YOUR KID IS UP" HERO BANNER ===
            Pulsing emerald when the parent's linked player is currently batting.
            Softer "on deck" variant when the kid is the next batter. Only shows
            during their own team's at-bat. Family-tier locked: non-premium
            parents see the upsell teaser instead. */}
        {kidAtBat && (
          parentIsPremium ? (
            <Card
              className="border-0 shadow-2xl overflow-hidden relative"
              data-testid="my-kid-at-bat-banner"
              style={{
                background: 'linear-gradient(135deg, #0E6B36 0%, #2EBE6B 100%)',
                border: '2px solid #F5C455',
                animation: 'pulse-green 2s infinite',
              }}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="shrink-0 w-16 h-16 rounded-2xl text-white flex items-center justify-center text-2xl font-black shadow-lg"
                  style={{ background: 'rgba(10, 19, 34, 0.55)', border: '2px solid #F5C455', fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {kidAtBat.jersey_number || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#F5C455' }}>⚾ YOUR KID IS UP</p>
                  <p className="text-2xl md:text-3xl font-extrabold text-white truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {kidAtBat.name} is batting
                  </p>
                  <p className="text-emerald-50 text-xs">#{currentBatterIndex + 1} in order · {team?.name}</p>
                </div>
                {(game?.current_balls > 0 || game?.current_strikes > 0) && (
                  <div className="text-right shrink-0">
                    <p className="text-4xl font-black text-white font-mono leading-none">{game.current_balls || 0}-{game.current_strikes || 0}</p>
                    <p className="text-emerald-50 text-[10px] mt-0.5">COUNT</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <FamilyUpsellCard
              feature={`${kidAtBat.name} is up right now!`}
              blurb="See a pulsing banner the moment your kid steps to the plate · also unlocks on-deck push alerts."
              testid="my-kid-at-bat-locked"
            />
          )
        )}
        {!kidAtBat && kidOnDeck && (
          parentIsPremium ? (
            <Card className="border-0 shadow-lg overflow-hidden"
              data-testid="my-kid-on-deck-banner"
              style={{
                background: 'linear-gradient(135deg, #131E36 0%, #1A2542 100%)',
                border: '1px solid rgba(46, 190, 107, 0.55)',
              }}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="shrink-0 w-11 h-11 rounded-xl text-white flex items-center justify-center text-base font-extrabold"
                  style={{ background: '#0E6B36', fontFamily: 'Barlow Condensed, sans-serif' }}>
                  {kidOnDeck.jersey_number || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#2EBE6B' }}>ON DECK</p>
                  <p className="text-base font-bold text-white truncate">
                    {kidOnDeck.name} — up next
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <FamilyUpsellCard
              feature={`${kidOnDeck.name} is on deck`}
              blurb="On-deck heads-up · YOUR KID IS UP banner the moment they step to the plate."
              testid="my-kid-on-deck-locked"
            />
          )
        )}

        {/* Scoreboard */}
        <Scoreboard
          homeTeam={game?.is_home ? team?.name : game?.opponent_name}
          awayTeam={game?.is_home ? game?.opponent_name : team?.name}
          homeScore={game?.home_score || 0}
          awayScore={game?.away_score || 0}
          inning={game?.current_inning || 1}
          isTopInning={isTopInning}
          outs={game?.current_outs || 0}
          innings={game?.innings || []}
          opponentInnings={game?.opponent_innings || []}
          isHome={game?.is_home}
        />

        {/* === ACTIVE PLAYS TICKER ===
            The most recent 4 plays scrolling continuously left under the
            scoreboard. Parents glancing from across town pick up the game
            rhythm without reading a full play log. Pauses on hover, hidden
            when the game is complete (the Hype Reel takes that real estate). */}
        {!game?.is_complete && Array.isArray(game?.play_log) && game.play_log.length > 0 && (
          <div
            className="overflow-x-hidden relative rounded-lg"
            data-testid="active-plays-ticker"
            style={{
              background: 'linear-gradient(90deg, rgba(14, 107, 54, 0.18) 0%, rgba(26, 37, 66, 0.85) 60%)',
              border: '1px solid rgba(46, 190, 107, 0.35)',
              maskImage: 'linear-gradient(90deg, transparent 0, #000 28px, #000 calc(100% - 28px), transparent 100%)',
              WebkitMaskImage: 'linear-gradient(90deg, transparent 0, #000 28px, #000 calc(100% - 28px), transparent 100%)',
            }}
          >
            {/* Live dot label (sits above the marquee, doesn't move) */}
            <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 z-10 px-1.5 py-0.5 rounded"
              style={{ background: 'rgba(10, 19, 34, 0.85)' }}>
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ background: '#2EBE6B' }} />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ background: '#2EBE6B' }} />
              </span>
              <span className="text-[9px] uppercase tracking-widest font-bold" style={{ color: '#2EBE6B' }}>LIVE</span>
            </div>
            {/* Sound toggle — Family-tier feature. Non-premium parents see a
                locked icon that opens the upgrade dialog when tapped. Default
                = muted; the click that turns it ON also unlocks the AudioContext
                (browsers require a user gesture before audio plays). */}
            {parentIsPremium ? (
              <button
                type="button"
                data-testid="active-plays-sound-toggle"
                aria-pressed={soundsOn}
                aria-label={soundsOn ? 'Mute play sounds' : 'Unmute play sounds'}
                onClick={(e) => {
                  e.stopPropagation();
                  const next = !soundsOn;
                  playSounds.setEnabled(next);
                  setSoundsOn(next);
                  if (next) playSounds.hit();
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-7 h-7 rounded-full transition-all hover:scale-110"
                style={{
                  background: soundsOn ? 'rgba(46, 190, 107, 0.25)' : 'rgba(10, 19, 34, 0.85)',
                  border: `1px solid ${soundsOn ? '#2EBE6B' : 'rgba(148, 163, 184, 0.4)'}`,
                }}
                title={soundsOn ? 'Tap to mute play sounds' : 'Tap to hear a bat-crack on hits and a thunk on outs'}
              >
                {soundsOn
                  ? <Volume2 size={14} style={{ color: '#2EBE6B' }} />
                  : <VolumeX size={14} className="text-slate-400" />}
              </button>
            ) : isNativeApp() ? null : (
              <button
                type="button"
                data-testid="active-plays-sound-locked"
                aria-label="Unlock play sounds with Family"
                onClick={(e) => { e.stopPropagation(); setShowFamilyUpsell(true); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-7 h-7 rounded-full transition-all hover:scale-110"
                style={{ background: 'rgba(10, 19, 34, 0.85)', border: '1px solid rgba(245, 196, 85, 0.5)' }}
                title="🔒 Family · $4.99/mo — Hear hits & outs in real time"
              >
                <Lock size={12} style={{ color: '#F5C455' }} />
              </button>
            )}
            {/* Marquee — newest first, duplicated for seamless loop */}
            <div className="ds-marquee gap-2 py-2 pl-16 pr-10" style={{ animationDuration: '28s' }} aria-live="off">
              {(() => {
                const recent = game.play_log.slice(-4).reverse();
                const items = [...recent, ...recent];
                return items.map((p, i) => {
                  const desc = p.description || p.text || '';
                  // Pick an accent color by play category
                  const lower = desc.toLowerCase();
                  const isHit = /(single|double|triple|home run|hr\b|hit)/.test(lower);
                  const isOut = /(\bout\b|strikeout|\bk\b|fly out|ground out|line out|pop out|infield fly|triple play|caught|tagged)/.test(lower);
                  const isWalk = /(walk|bb\b|hbp|hit by pitch|interference|balk|obstruction)/.test(lower);
                  const isScore = /\(\d+ ?r\)/.test(lower) || /\bscore[ds]?\b/.test(lower);
                  const accent = isScore ? '#F5C455' : isHit ? '#2EBE6B' : isWalk ? '#60A5FA' : isOut ? '#F87171' : '#94A3B8';
                  // The very newest play sits at index 0. Re-keying with flashKey
                  // forces React to remount → the CSS keyframe animation replays.
                  const isNewest = i === 0;
                  return (
                    <div key={isNewest ? `flash-${flashKey}` : i}
                      className={cn(
                        "flex items-center gap-2 px-3 py-1 rounded-md text-xs font-medium whitespace-nowrap shrink-0 text-white",
                        isNewest && flashKey > 0 && "ds-play-flash"
                      )}
                      style={{ background: 'rgba(10, 19, 34, 0.55)', border: `1px solid ${accent}55` }}
                      aria-hidden={i >= recent.length}
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
                      <span className="text-slate-300">Inn {p.inning}</span>
                      <span className="text-slate-500">·</span>
                      <span>{desc}</span>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}

        {/* Share Live Score — promoted to brand gold gradient + bigger tap target */}
        {!game?.is_complete && (
          <Button
            size="lg"
            className="w-full font-bold text-stone-900 shadow-lg text-sm md:text-base"
            data-testid="share-live-score-btn"
            style={{ background: 'linear-gradient(135deg, #F5C455 0%, #D4A03A 100%)' }}
            onClick={() => {
              const shareUrl = `${window.location.origin}/api/share/live/${teamId}/${gameId}`;
              const isHome2 = game?.is_home;
              const h = game?.home_score || 0;
              const a = game?.away_score || 0;
              const homeTeam = isHome2 ? team?.name : game?.opponent_name;
              const awayTeam = isHome2 ? game?.opponent_name : team?.name;
              const inn = game?.current_inning || 1;
              const state = game?.is_complete ? 'FINAL' : game?.is_canceled ? 'CANCELED' : `${(game?.is_top_inning !== false) ? 'Top' : 'Bot'} ${inn}`;
              const title = `${homeTeam} ${h}, ${awayTeam} ${a} · ${state}`;
              if (navigator.share) {
                navigator.share({ title, text: title, url: shareUrl }).catch(() => {});
              } else {
                navigator.clipboard.writeText(shareUrl);
                toast.success('Live score link copied!');
              }
            }}
          >
            <Share2 size={16} className="mr-2" /> Share Live Score with Family
          </Button>
        )}

        {/* Season Milestones & Streaks — auto-scrolling marquee.
            We duplicate the chip array so the CSS animation can translate -50%
            and loop seamlessly. Pauses on hover so parents can read any highlight
            they spot. Manual horizontal scroll still works via overflow-x-auto. */}
        {milestones.length > 0 && (
          <div className="overflow-x-hidden relative" data-testid="milestones-banner"
            style={{
              maskImage: 'linear-gradient(90deg, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%)',
              WebkitMaskImage: 'linear-gradient(90deg, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%)',
            }}>
            <div className="ds-marquee gap-2 pb-1" aria-live="off">
              {[...milestones.slice(0, 6), ...milestones.slice(0, 6)].map((m, i) => {
                const Icon = m.icon === 'flame' ? Flame : m.icon === 'zap' ? Zap : m.icon === 'star' ? Star : m.icon === 'trophy' ? Trophy : Target;
                const colors = m.type === 'streak'
                  ? 'border text-orange-200'
                  : 'border text-amber-200';
                const bgStyle = m.type === 'streak'
                  ? { background: 'rgba(234, 88, 12, 0.18)', borderColor: 'rgba(234, 88, 12, 0.45)' }
                  : { background: 'rgba(212, 160, 58, 0.18)', borderColor: 'rgba(212, 160, 58, 0.45)' };
                return (
                  <div key={i} className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold whitespace-nowrap shrink-0", colors)}
                    style={bgStyle}
                    aria-hidden={i >= milestones.slice(0, 6).length}>
                    <Icon size={12} />
                    <span>{m.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Who's Hot Card - scouting preview before game */}
        {teamId && <WhosHotCard teamId={teamId} teamName={team?.name} compact={true} />}

        {/* Main content: 2 columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* Left: Diamond + Pitcher */}
          <div className="space-y-3">
            <DiamondField
              runners={game?.runners_on_base}
              fieldPositions={game?.field_positions}
              players={team?.players}
              currentPitcher={currentPitcher}
              pitchCount={game?.pitch_count}
              opponentFieldPositions={game?.opponent_field_positions}
              opponentPitcherName={game?.opponent_pitcher_name}
              opponentPitchCount={game?.opponent_pitch_count}
              myTeamBatting={myTeamBatting}
              teamName={team?.name}
              oppName={game?.opponent_name}
              totalScore={(game?.home_score || 0) + (game?.away_score || 0)}
            />

            {/* Current Batter Card */}
            {(myTeamBatting && currentBatter) ? (
              <Card className="border-0 bg-gradient-to-r from-green-900/40 to-stone-800 shadow-lg border-l-4 border-l-green-500" data-testid="current-batter-card">
                <CardContent className="py-3 px-4">
                  <p className="text-stone-500 text-[9px] uppercase tracking-wider mb-1">Now Batting - {team?.name}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg bg-green-700 text-white flex items-center justify-center text-lg font-extrabold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {currentBatter.jersey_number || '?'}
                    </div>
                    <div className="flex-1">
                      <p className="text-lg font-bold text-white">{currentBatter.name}</p>
                      <p className="text-stone-400 text-xs">#{currentBatterIndex + 1} in order</p>
                    </div>
                    {(game?.current_balls > 0 || game?.current_strikes > 0) && (
                      <div className="text-right">
                        <p className="text-2xl font-black text-amber-400 font-mono">{game.current_balls || 0}-{game.current_strikes || 0}</p>
                        <p className="text-stone-600 text-[8px]">COUNT</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : (!myTeamBatting && currentOppBatter) ? (
              <Card className="border-0 bg-gradient-to-r from-red-900/40 to-stone-800 shadow-lg border-l-4 border-l-red-500" data-testid="current-batter-card">
                <CardContent className="py-3 px-4">
                  <p className="text-stone-500 text-[9px] uppercase tracking-wider mb-1">Now Batting - {game?.opponent_name}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg bg-red-700 text-white flex items-center justify-center text-lg font-extrabold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                      {currentOppBatter.number || '?'}
                    </div>
                    <div className="flex-1">
                      <p className="text-lg font-bold text-white">{currentOppBatter.name}</p>
                      <p className="text-stone-400 text-xs">#{opponentBatterIndex + 1} in order</p>
                    </div>
                    {(game?.current_balls > 0 || game?.current_strikes > 0) && (
                      <div className="text-right">
                        <p className="text-2xl font-black text-amber-400 font-mono">{game.current_balls || 0}-{game.current_strikes || 0}</p>
                        <p className="text-stone-600 text-[8px]">COUNT</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ) : null}

            {/* At-Bat Tracker */}
            {game?.play_log?.some(e => {
              const d = e.description || '';
              return d.startsWith('Ball') || d.startsWith('Strike') || d.startsWith('Foul Ball');
            }) && (
              parentIsPremium ? (
                <Card className="border-0 shadow-lg" data-testid="parent-pitch-chart" style={{ background: '#131E36', border: '1px solid rgba(212, 160, 58, 0.15)' }}>
                  <CardHeader className="pb-1">
                    <CardTitle className="text-xs flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: '#F5C455' }}>
                      AT-BAT TRACKER
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3">
                    <PitchZoneChart playLog={game.play_log} currentInning={game.current_inning || 1} isTopInning={isTopInning} />
                  </CardContent>
                </Card>
              ) : (
                <FamilyUpsellCard
                  feature="At-Bat Tracker · ball / strike zone visualization"
                  blurb="See every pitch location for the current batter."
                  testid="pitch-chart-locked"
                />
              )
            )}

            {/* Spray Chart - Where Balls Were Hit (Coach must be Premium) */}
            {teamPremium && game?.at_bats?.some(ab => ab.spray_x != null) && (
              <Card className="border-0 bg-stone-800 shadow-lg" data-testid="parent-spray-chart">
                <CardHeader className="pb-1">
                  <CardTitle className="text-green-400 text-xs flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    WHERE THEY HIT IT
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3">
                  <svg viewBox="0 0 100 100" className="w-full max-w-[250px] mx-auto aspect-square rounded-lg overflow-hidden" style={{ background: '#166534' }}>
                    <path d="M 5 95 Q 5 5, 50 5 Q 95 5, 95 95" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
                    <path d="M 50 85 L 75 60 L 50 35 L 25 60 Z" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="0.5" />
                    <line x1="50" y1="85" x2="5" y2="15" stroke="rgba(255,255,255,0.2)" strokeWidth="0.3" />
                    <line x1="50" y1="85" x2="95" y2="15" stroke="rgba(255,255,255,0.2)" strokeWidth="0.3" />
                    {game.at_bats.filter(ab => ab.spray_x != null).map((ab, i) => {
                      const colors = { single: '#22c55e', double: '#3b82f6', triple: '#f59e0b', home_run: '#ef4444', error_1: '#a855f7', error_2: '#a855f7', error_3: '#a855f7' };
                      const color = colors[ab.result] || '#94a3b8';
                      const playerName = team?.players?.find(p => p.id === ab.batter_id)?.name?.split(' ')[0] || '';
                      return (
                        <g key={i}>
                          <circle cx={ab.spray_x} cy={ab.spray_y} r={2.5} fill={color} stroke="white" strokeWidth="0.3" opacity="0.9" />
                          <text x={ab.spray_x} y={ab.spray_y - 4} textAnchor="middle" fill="white" fontSize="2.5" opacity="0.7">{playerName}</text>
                        </g>
                      );
                    })}
                  </svg>
                  <div className="flex flex-wrap justify-center gap-3 mt-2">
                    {[['1B','#22c55e'],['2B','#3b82f6'],['3B','#f59e0b'],['HR','#ef4444'],['E','#a855f7']].map(([label, color]) => (
                      <div key={label} className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                        <span className="text-stone-400 text-[9px]">{label}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: Lineup + Play-by-play */}
          <div className="space-y-3">
            <BattingLineup
              battingOrder={game?.batting_order}
              players={team?.players}
              opponentLineup={game?.opponent_lineup}
              playLog={game?.play_log}
              currentBatterIndex={currentBatterIndex}
              opponentBatterIndex={opponentBatterIndex}
              isTopInning={isTopInning}
              isHome={isHome}
              teamName={team?.name}
              oppName={game?.opponent_name}
            />

            {/* BOX SCORE */}
            <BoxScore game={game} team={team} />

            {/* Game Summary Story */}
            {game?.game_summary_story && (
              <Card className="border-0 bg-gradient-to-br from-stone-800 to-stone-800/80 shadow-lg border-l-4 border-l-amber-500" data-testid="game-summary-story">
                <CardHeader className="pb-1">
                  <CardTitle className="text-amber-400 text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    <Trophy size={14} /> GAME RECAP
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-stone-300 text-sm leading-relaxed whitespace-pre-wrap">{game.game_summary_story}</p>
                </CardContent>
              </Card>
            )}

            <PlayLog playLog={game?.play_log} gameId={gameId} parentName={parentName} />

            {/* Parent Name + My Kids + Notifications */}
            <Card className="border-0 bg-stone-800/60 shadow-lg" data-testid="parent-settings-card">
              <CardContent className="py-3 px-4 space-y-3">
                {!parentName ? (
                  <div className="space-y-2">
                    <p className="text-stone-400 text-xs">Enter your name to like plays, link your kids, and get notifications</p>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const name = e.target.elements.pname.value.trim();
                      if (name) { setParentName(name); localStorage.setItem('dmp_parent_name', name); toast.success(`Welcome, ${name}!`); }
                    }} className="flex gap-2">
                      <input name="pname" placeholder="Your name" className="flex-1 bg-stone-700 border border-stone-600 rounded px-3 py-1.5 text-white text-sm" data-testid="parent-name-input" />
                      <Button type="submit" size="sm" className="bg-green-700 hover:bg-green-600 text-xs" data-testid="set-parent-name-btn">Join</Button>
                    </form>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-green-500" />
                        <span className="text-stone-300 text-sm">{parentName}</span>
                        <button onClick={() => { setParentName(''); localStorage.removeItem('dmp_parent_name'); setMyKids([]); setShareToken(null); }} className="text-stone-600 text-[10px] hover:text-stone-400">(change)</button>
                      </div>
                      {pushSupported && !isNativeApp() && (
                        teamPremium === false ? (
                          <Button size="sm" variant="outline"
                            className="text-xs border-amber-700 text-amber-400 hover:text-amber-300 hover:bg-amber-900/20"
                            data-testid="push-upgrade-btn"
                            onClick={async () => {
                              const shareText = `Hey coach — I'm following ${team?.name || 'the team'} on Diamond Scorer. Can you upgrade to Premium so I can get push alerts for big plays? Upgrade here: ${window.location.origin}/upgrade`;
                              try {
                                if (navigator.share) await navigator.share({ title: 'Diamond Scorer', text: shareText });
                                else { await navigator.clipboard.writeText(shareText); toast.success('Message copied — text your coach!'); }
                              } catch {}
                            }}>
                            <Lock size={12} className="mr-1" /> Ask coach for alerts
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline"
                            className={cn("text-xs border-stone-600", pushSub ? "text-green-400" : "text-stone-400")}
                            data-testid="push-toggle-btn"
                            onClick={async () => {
                              if (pushSub) {
                                await pushUnsubscribe();
                                toast.info('Notifications off');
                              } else {
                                await pushSubscribe(teamId, myKids, parentName);
                                toast.success('Notifications enabled!');
                              }
                            }}>
                            {pushSub ? <><BellOff size={12} className="mr-1" /> Notifications On</> : <><Bell size={12} className="mr-1" /> Enable Alerts</>}
                          </Button>
                        )
                      )}
                    </div>

                    {/* My Kids Section */}
                    <div className="border-t border-stone-700 pt-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                          <Star size={12} className="text-amber-500" />
                          <span className="text-stone-300 text-xs font-semibold">MY KIDS</span>
                          {myKids.length > 0 && <span className="text-amber-400 text-[10px]">({myKids.length} linked)</span>}
                        </div>
                        <Button size="sm" variant="ghost" className="text-xs text-amber-400 hover:text-amber-300 h-6 px-2"
                          data-testid="select-kids-btn"
                          onClick={() => setShowKidPicker(!showKidPicker)}>
                          {showKidPicker ? 'Done' : myKids.length > 0 ? 'Edit' : 'Select Kids'}
                        </Button>
                      </div>
                      
                      {showKidPicker && team?.players && (
                        <div className="grid grid-cols-2 gap-1.5 mb-2" data-testid="kid-picker">
                          {team.players.map(p => {
                            const isSelected = myKids.includes(p.id);
                            return (
                              <button key={p.id}
                                data-testid={`kid-option-${p.id}`}
                                onClick={async () => {
                                  const newKids = isSelected ? myKids.filter(k => k !== p.id) : [...myKids, p.id];
                                  setMyKids(newKids);
                                  try {
                                    const res = await fetch(`${API}/api/highlights/link-kids`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ parent_name: parentName, team_id: teamId, player_ids: newKids })
                                    });
                                    const data = await res.json();
                                    setShareToken(data.share_token);
                                    if (!isSelected) toast.success(`${p.name} linked!`);
                                  } catch {}
                                }}
                                className={cn(
                                  'flex items-center gap-2 px-3 py-2 rounded-lg text-left text-xs transition-all',
                                  isSelected ? 'bg-amber-700/40 border border-amber-600 text-white' : 'bg-stone-700/50 border border-stone-600 text-stone-400 hover:border-stone-500'
                                )}>
                                <div className={cn(
                                  'w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0',
                                  isSelected ? 'bg-amber-600 text-white' : 'bg-stone-600 text-stone-400'
                                )}>
                                  {p.jersey_number || '?'}
                                </div>
                                <span className="truncate">{p.name}</span>
                                {isSelected && <Star size={10} className="text-amber-400 ml-auto shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* Highlight Reel Link */}
                      {myKids.length > 0 && shareToken && (
                        <Button size="sm" className="w-full bg-gradient-to-r from-amber-700 to-amber-600 hover:from-amber-600 hover:to-amber-500 text-white text-xs"
                          data-testid="view-highlight-reel-btn"
                          onClick={() => window.open(`/highlights/${shareToken}`, '_blank')}>
                          <Star size={12} className="mr-1.5" />
                          View Highlight Reel
                          <ExternalLink size={10} className="ml-1.5 opacity-60" />
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* === Family-trial chip — visible only during the 3-day kid-link taste.
             Tap it any time to lock in the $4.99 subscription. */}
        {familyTrialDaysLeft > 0 && !parentInfo?.subscription_active && !isNativeApp() && (
          <button
            type="button"
            data-testid="family-trial-chip"
            onClick={() => setShowFamilyUpsell(true)}
            className="fixed bottom-4 right-4 z-40 rounded-full px-3 py-1.5 text-xs font-bold shadow-lg flex items-center gap-2 transition-all hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, #F5C455 0%, #D4A03A 100%)',
              color: '#0A1322',
            }}
          >
            ⏳ Family trial · {familyTrialDaysLeft} day{familyTrialDaysLeft === 1 ? '' : 's'} left
            <span className="text-[10px] opacity-80">Keep it →</span>
          </button>
        )}

        {/* Family upgrade dialog — shared by all locked-feature triggers. */}
        <FamilyUpgradeDialog open={showFamilyUpsell} onOpenChange={setShowFamilyUpsell} />
      </div>
    </div>
  );
}
